import { isObject } from '@vaaas/ts-validate';
import { Unary } from '@vaaas/fpts/data';
import { PromiseResult } from '@vaaas/fpts/promise-result';
import { Result } from '@vaaas/fpts/result';
import * as qs from 'qs';
import { Email } from '@phi/newtype';

export type Parser<T> = Unary<unknown, Result<T>>;
export type AsyncParser<T> = Unary<unknown, PromiseResult<T>>;
export type AnyParser<T> = Unary<unknown, Result<T> | PromiseResult<T>>;

export type ParserResult<T extends (AsyncParser<any> | Parser<any>)> =
    T extends AsyncParser<infer U>
    ? U
    : T extends Parser<infer U>
    ? U
    : never;

export class ValidationError extends Error {
    constructor(message = 'Validation error') {
        super(message);
        // @ts-ignore
        this.code = 400;
    }
}

const isInteger = (x: unknown): x is number => Number.isInteger(x);

const isArray = (x: unknown): x is unknown[] => Array.isArray(x);

/** passes a value through a parser, but additionally accepts null or undefined
 *
 * in other words, turns any parser into an optional parser
 *
 * @param f any parser
 * @param x any value to be parsed
 * @returns a parser that also accepts null and undefined
 *
 * @example const vector_2d_or_undefined = Optional(Tuple[Integer, Integer]);
 */
export const Optional = <T>(f: Parser<T>): Parser<T | undefined> => (x) => {
    if (x === undefined || x === null)
        return undefined;
    else
        return f(x);
};

export const Structure = <T extends Record<string, any>>(fs: { [K in keyof T]: Parser<T[K]> }) => <U>(x: U): Result<T & U> => {
    if (!x) return new ValidationError();
    if (typeof x !== 'object') return new ValidationError();
    for (const [k, f] of Object.entries(fs)) {
        // @ts-ignore
        const v: unknown = x[k];
        const parsed = f(v);
        if (parsed instanceof Error)
            return parsed;
        else
            // @ts-ignore
            x[k] = parsed;
    }
    return x as any;
};

export const AsyncStructure = <T extends Record<string, any>>(fs: { [K in keyof T]: AnyParser<T[K]> }) => async <U>(x: U): PromiseResult<T & U> => {
    if (!x) return new ValidationError();
    if (typeof x !== 'object') return new ValidationError();
    for (const [k, f] of Object.entries(fs)) {
        // @ts-ignore
        const v: unknown = x[k];
        let parsed = f(v);
        if (parsed instanceof Promise)
            parsed = await parsed;
        if (parsed instanceof Error)
            return parsed;
        else
            // @ts-ignore
            x[k] = parsed;
    }
    return x as any;
};

export const UnknownStructure: Parser<Record<string, unknown>> = x =>
    isObject(x)
        ? x as Record<string, unknown>
        : new ValidationError();

export const Text: Parser<string> = (x) => typeof x === 'string' ? x: new ValidationError();

export const BoundedText = (min = 0, max = Infinity): Parser<string> => (x) => {
    if (typeof x !== 'string')
        return new ValidationError();
    else if (x.length < min || x.length > max)
        return new ValidationError();
    else
        return x;
};

export const QueryString: Parser<Record<string, string | undefined>> = (x: unknown) => {
    if (typeof x !== 'string')
        return new ValidationError();
    else
        return qs.parse(x) as any;
};

/** given several parsers, return the result of the first valid parser
 *
 * @param fs the parsers
 * @param x the unknown input to parse
 * @returns the result of the first matching parser
 */
export function Union<A, B>(
    a: Parser<A>,
    b: Parser<B>,
): Parser<A|B>;
export function Union<A, B, C>(
    a: Parser<A>,
    b: Parser<B>,
    c: Parser<C>,
): Parser<A | B | C>;
export function Union(...fs: Parser<any>[]) {
    return function (x: unknown) {
        for (const f of fs) {
            const y = f(x);
            if (!(y instanceof Error))
                return y;
        }
        return new ValidationError();
    };
}

export function Exactly<T extends string | number | boolean | null | undefined | symbol>(x: T): Parser<T> {
    return function (y: unknown) {
        if (y === x)
            return y as T;
        else
            return new ValidationError();
    };
}

export const Numeric: Parser<number> = (x: unknown) =>
    typeof x === 'number' && Number.isFinite(x) ? x : new ValidationError();

export const Integer: Parser<number> = (x: unknown) =>
    isInteger(x) ? x : new ValidationError();

export const Natural = (x: unknown) =>
    isInteger(x) && x >= 0 ? x : new ValidationError();

export const AnyList: Parser<unknown[]> = (x: unknown) =>
    isArray(x) ? x : new ValidationError();

export const EmptyList: Parser<[]> = (x: unknown) =>
    isArray(x) && x.length === 0 ? x as [] : new ValidationError();

export function List<T>(f: Parser<T>, min = 0, max = Infinity): Parser<Array<T>> {
    return function (xs: unknown) {
        if (!isArray(xs))
            return new ValidationError();
        else if (xs.length < min || xs.length > max)
            return new ValidationError();
        for (let i = 0; i < xs.length; i++) {
            const x = f(xs[i]);
            if (x instanceof Error)
                return x;
            else
                xs[i] = x;
        }
        return xs as T[];
    };
}

export function Tuple<A>(
    a: Parser<A>,
): Parser<[A]>;
export function Tuple<A, B>(
    a: Parser<A>,
    b: Parser<B>,
): Parser<[A, B]>;
export function Tuple<A, B, C>(
    a: Parser<A>,
    b: Parser<B>,
    c: Parser<C>,
): Parser<[A, B, C]>;
export function Tuple<A, B, C, D>(
    a: Parser<A>,
    b: Parser<B>,
    c: Parser<C>,
    d: Parser<D>,
): Parser<[A, B, C, D]>;
export function Tuple<A, B, C, D, E>(
    a: Parser<A>,
    b: Parser<B>,
    c: Parser<C>,
    d: Parser<D>,
    e: Parser<E>,
): Parser<[A, B, C, D, E]>;
export function Tuple(...fs: Parser<any>[]): Parser<Array<any>> {
    return function (xs) {
        if (!isArray(xs) || xs.length !== fs.length)
            return new ValidationError();
        for (let i = 0; i < fs.length; i++) {
            const x = fs[i]!(xs[i]);
            if (x instanceof Error)
                return x;
            else
                xs[i] = x;
        }
        return xs;
    };
}

export function AsyncTuple<A>(
    a: AnyParser<A>,
): AsyncParser<[A]>;
export function AsyncTuple<A, B>(
    a: AnyParser<A>,
    b: AnyParser<B>,
): AsyncParser<[A, B]>;
export function AsyncTuple<A, B, C>(
    a: AnyParser<A>,
    b: AnyParser<B>,
    c: AnyParser<C>,
): AsyncParser<[A, B, C]>;
export function AsyncTuple<A, B, C, D>(
    a: AnyParser<A>,
    b: AnyParser<B>,
    c: AnyParser<C>,
    d: AnyParser<D>,
): AsyncParser<[A, B, C, D]>;
export function AsyncTuple<A, B, C, D, E>(
    a: AnyParser<A>,
    b: AnyParser<B>,
    c: AnyParser<C>,
    d: AnyParser<D>,
    e: AnyParser<E>,
): AsyncParser<[A, B, C, D, E]>;
export function AsyncTuple(...fs: Parser<any>[]): AsyncParser<Array<any>> {
    return async function (xs) {
        if (!isArray(xs) || xs.length !== fs.length)
            return new ValidationError();
        for (let i = 0; i < fs.length; i++) {
            let x = fs[i]!(xs[i]);
            if (x instanceof Promise)
                x = await x;
            if (x instanceof Error)
                return x;
            else
                xs[i] = x;
        }
        return xs;
    };
}

export const Bool: Parser<true | false> = (x) => {
    if (x === true || x === false)
        return x;
    else
        return new ValidationError();
};

const email_regex = new RegExp('^[a-zA-Z0-9-_\\.+]+@[a-zA-Z0-9-\\.]+$');
export const EmailParser: Parser<Email> = x => {
    return typeof x === 'string' && email_regex.test(x)
        ? (x as any as Email)
        : new ValidationError();
};
